// One-shot things that happened during a sim step. Render and audio drain
// these each frame; they never feed back into the sim.

/**
 * How a boarding fight stands: hauling alongside, fighting at the rail, a
 * foothold on her deck, driving them aft, the defence breaking; and how it
 * ended - the boarders thrown back, or her deck carried.
 */
export type BoardingPhase = 'hauling' | 'rail' | 'foothold' | 'driving' | 'breaking' | 'repelled' | 'carried';

export type SimEvent =
  | { type: 'lightning'; x: number; z: number; distance: number; intensity: number }
  | { type: 'cannonFire'; index: number; x: number; y: number; z: number; dx: number; dy: number; dz: number; vessel: number; weight: number }
  | { type: 'splash'; x: number; y: number; z: number; size: number; kind: 'ball' | 'slam' | 'object' | 'wreck' }
  | { type: 'slam'; x: number; y: number; z: number; intensity: number; side: number }
  | { type: 'washOver'; x: number; y: number; z: number; intensity: number; side: number; localX: number }
  | { type: 'creak'; intensity: number; x: number; y: number; z: number; pitch: number }
  | { type: 'sailFill'; sail: string; intensity: number; x: number; y: number; z: number }
  | { type: 'objectHit'; intensity: number; x: number; y: number; z: number }
  | { type: 'bell'; strikes: number }
  /** Shot striking a ship: what it hit and how hard (1 = 12-pdr at the muzzle), and whose shot it was (-1 nobody's). */
  | { type: 'impact'; x: number; y: number; z: number; what: 'hull' | 'rail' | 'deck' | 'mast' | 'sail' | 'rigging' | 'gun' | 'rudder' | 'crew' | 'boat'; energy: number; vessel: number; nx: number; ny: number; nz: number; by?: number }
  | { type: 'explosion'; x: number; y: number; z: number; size: number; vessel: number }
  | { type: 'collapse'; x: number; y: number; z: number; what: string; size: number }
  | { type: 'mastFall'; x: number; y: number; z: number; mast: string; level: number; side: number; vessel?: number }
  /** A sail shot through (level 1: half gone) or in ribbons (2), by whose shot. */
  | { type: 'sailTorn'; x: number; y: number; z: number; vessel: number; sail: string; level: 1 | 2; by: number }
  /** A ball in at her bow or stern and along her decks (a rake), by whose gun. */
  | { type: 'raked'; x: number; y: number; z: number; vessel: number; end: 'bow' | 'stern'; by: number }
  /** A spare spar sent up in place of one lost (level: what was lost before, 3 = a jury mast for the lower mast). */
  | { type: 'sparRigged'; vessel: number; mast: string; level: number }
  /** A sail made good again by the sailmakers (or a spare bent on). */
  | { type: 'sailMended'; vessel: number; sail: string }
  /** Grog served out to put heart into the men (`left`: casks remaining). */
  | { type: 'rally'; vessel: number; left: number; lift: number }
  | { type: 'fire'; x: number; y: number; z: number; vessel: number; started: boolean }
  | { type: 'casualty'; x: number; y: number; z: number; vessel: number; killed: boolean }
  | { type: 'shipSinking'; vessel: number; x: number; z: number }
  | { type: 'shipSunk'; vessel: number; x: number; z: number; cls: string }
  | { type: 'struck'; vessel: number }
  | { type: 'boarding'; a: number; b: number; x: number; z: number }
  /** The boarders hold too much of our deck for the lines to be cut. */
  | { type: 'cutRefused'; vessel: number }
  /** A boarding fight turning: who holds the deck of the ship boarded (see contact.ts). */
  | { type: 'boardingTurn'; attacker: number; defender: number; phase: BoardingPhase; hold: number }
  | { type: 'captured'; vessel: number; by: number }
  | { type: 'collision'; x: number; y: number; z: number; energy: number }
  | { type: 'sighted'; vessel: number; target: number }
  | { type: 'signal'; vessel: number; x: number; y: number; z: number }
  /** A ship moved bodily (the dev chart): where to, and by how much. */
  | { type: 'teleport'; vessel: number; x: number; z: number; dx: number; dy: number; dz: number }
  /** What a developer tool did (or why it couldn't). */
  | { type: 'dev'; text: string; ok: boolean };

export class SimEvents {
  private list: SimEvent[] = [];

  push(e: SimEvent): void {
    if (this.list.length < 512) this.list.push(e);
  }

  /** Events queued since a mark (without draining). */
  since(mark: number): SimEvent[] {
    return this.list.slice(mark);
  }

  get mark(): number {
    return this.list.length;
  }

  /** Return and clear everything queued since the last drain. */
  drain(): SimEvent[] {
    const out = this.list;
    this.list = [];
    return out;
  }
}
